import axios from '@/plugins/axios';
import Vue from "vue";
import { mapGetters, mapActions, mapState } from "vuex";
import { isMobile as _isMobile } from "mobile-device-detect";
import Help from "@/modals/help.vue";
import ConfirmEmail from "@/modals/confirmemail";
import utils from "@/utils.js";
import { isRetailerAlwaysOnFeature } from "@/utils/features";

export default {
    methods: {
        ...mapActions("mcl", ["showModal", "hideModal"]),
        ...mapActions("settings", ["saveEntity",]),
        ...mapActions("session", ["addAlert"]),
        getStockName(stock) {
            return this.$utils.getStockName(stock);
        },
        getDeliverySiteName(deliverysite) {
            if (!deliverysite) {
                return "";
            }
            if (deliverysite.stock) {
                const stock = deliverysite.stock_data;
                if (!stock) {
                    return deliverysite.name || "";
                }
                const establishmentName = stock.establishment_data?.name;
                if (
                    establishmentName &&
                    stock.name &&
                    stock.name.trim() !== establishmentName.trim()
                ) {
                    return `${stock.name} (${establishmentName})`;
                }
                return this.getStockName(stock);
            }
            if (!deliverysite.customer && !deliverysite.owned) {
                return `${deliverysite.name} (${deliverysite.entity_data?.name || this.$translate('external')})`;
            }
            return deliverysite.name;
        },
        openHelp(helpContentId) {
            return this.showModal({
                component: Help,
                params: {
                    contentUniqueId: helpContentId,
                },
            });
        },
        userHasPermission(perm) {
            return this.$utils.userHasPermission(perm);
        },
        userCanCreateSaleProductFromProvider() {
            return (
                this.userHasPermission("core.add_product") &&
                this.userHasPermission("core.add_productlink")
            );
        },
        userCanCreateProviderProduct() {
            return this.userHasPermission("core.add_provider");
        },
        userCanViewProductLinks() {
            return (
                this.userHasPermission("core.view_productlink") ||
                this.userHasPermission("core.view_provider")
            );
        },
        userCanAddProductLink() {
            return this.userHasPermission("core.add_productlink");
        },
        userCanDeleteProductLink() {
            return this.userHasPermission("core.delete_productlink");
        },
        userCanImportProviderProducts() {
            return this.userHasPermission("core.change_provider");
        },
        userCanViewProviderCatalogEntries() {
            return (
                this.userHasPermission("catalog.view_providercatalog") ||
                this.userHasPermission("core.view_provider") ||
                this.userHasPermission("core.view_product")
            );
        },
        userCanViewSellCatalogEntries() {
            return this.userHasPermission("catalog.view_sellcatalog");
        },
        userCanChangeSellCatalogEntry() {
            return (
                this.userHasPermission("catalog.change_sellcatalog") ||
                this.userHasPermission("catalog.change_catalogentry")
            );
        },
        userCanDeleteSellCatalogEntry() {
            return (
                this.userHasPermission("catalog.delete_sellcatalog") ||
                this.userHasPermission("catalog.delete_catalogentry")
            );
        },
        userCanAssignProductSalesCatalogs() {
            return this.userCanChangeSellCatalogEntry();
        },
        userCanChangeProviderCatalogEntry() {
            return (
                this.userHasPermission("catalog.change_providercatalog") ||
                this.userCanChangeProviderPurchasePrices()
            );
        },
        userCanChangeProviderPurchasePrices() {
            return this.userHasPermission("core.change_provider");
        },
        userCanArchiveManagedProviderProduct() {
            return (
                this.userCanChangeProviderPurchasePrices() &&
                this.provider?.owner === this.current_entity_id &&
                !this.provider?.entity
            );
        },
        currentEntityHasFeature(code) {
            if (isRetailerAlwaysOnFeature(code, this.current_entity_persona)) {
                return true;
            }
            return this.current_entity_features.find(
                (f) => f.code == code && f.profile == this.current_entity_profile
            );
        },
        iconName(code) {
            if (!code) {
                throw new Error("iconName: code is required");
            }
            return utils.icon(code);
        },
        filtersEmpty(filters) {
            filters = Object.assign(filters);
            if (filters.offset) {
                delete filters.offset;
            }
            if (filters.limit) {
                delete filters.limit;
            }
            return Object.keys(filters).length == 0;
        },
        swalError(error) {
            this.$swal({
                title: this.$translate("error"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
        swalErrorForField(result, field) {
            this.$swal({
                title: this.$translate("error"),
                html: this.$utils.resultToSwalContentForField(this, result, field),
                type: "error",
            });
        },
        routeExists(routename) {
            if (!routename) {
                return false;
            }
            const findRoute = (routes) => {
                for (const route of routes) {
                    if (route.name === routename) {
                        return true;
                    }
                    if (route.children?.length && findRoute(route.children)) {
                        return true;
                    }
                }
                return false;
            };
            return findRoute(this.$router.options.routes);
        },
        price(value, currency) {
            if (value === null) {
                return null;
            }
            return utils.price(value, currency);
        },
        percent(value) {
            if (value === null) {
                return null;
            }
            return utils.percent(value);
        },
        decimal(value) {
            return utils.decimal(value);
        },
        number(value) {
            if (value === null) {
                return null;
            }
            return utils.number(value);
        },

        moment(date, format) {
            if (!date) {
                return "-";
            }
            return this.$moment(date).format(format || "LL");
        },
        checkEmailConfirmed() {
            if (this.current_user_identity_confirmed) {
                return true;
            }
            this.showModal({
                component: ConfirmEmail,
            }).then((result) => {
                this.$store.dispatch('session/fetchLoginInfo', null, { root: true });
                return;
            });
            return false;
        },
        async checkEntityInformationComplete() {
            if (!utils.isEntityInformationComplete(this.current_entity)) {
                const { default: EditEntity } = await import("@/modals/entity");
                const completeResult = await this.showModal({
                    component: EditEntity,
                    params: {
                        entity: this.current_entity,
                        title: Vue.prototype.$translate("completeAddress.title"),
                        instructions: Vue.prototype.$translate("completeAddress.instructions"),
                    },
                });
                if (!completeResult) {
                    return false;
                }
            }
            return true;
        }
    }
    ,
    computed: {
        ...mapGetters("session", { is_readonly: "readonly" }),
        ...mapGetters("session", [
            "current_user",
            "current_user_superuser",
            "current_user_mode",
            "current_user_permissions",
            "current_user_identity_confirmed",
            "current_user_id",
            "current_entity_profile",
            "current_entity_persona",
            "current_entity_features",
            "current_entity_id",
            "current_entity_settings",
            "current_entity",
            "current_entity_name",
            "current_entity_stats",
            "current_user_is_owner",
        ]),
        isOperator() {
            return this.current_entity_profile == "operator";
        },
        isCustomer() {
            return this.current_entity_profile == "customer";
        },
        hasParent() {
            return !!this.current_entity.parent;
        },
        isAgent() {
            return this.current_entity_profile == "agent";
        },
        isMobile() {
            return _isMobile;
        },
        isReadOnly() {
            return this.is_readonly;
        },
        /**
         * The cart is only used to create provider (purchase) orders, so it
         * requires the `write_providerorders` scope (here checked through its
         * canonical `order.add_providerorder` permission).
         */
        canUseCart() {
            return this.userHasPermission("order.add_providerorder");
        },
    }
    ,
};
